import { useEffect, useRef, useState, lazy, Suspense, Component } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, CalendarDays, Car, Loader2, LocateFixed, MapPin, MousePointerClick, Search, ShieldCheck, KeyRound, CalendarCheck, X, QrCode, Building2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import MobileAppsComingSoon from "../components/MobileAppsComingSoon";
import SpotCard from "../components/SpotCard";
import { CardSkeleton } from "../components/States";
import { DateField, TimeField } from "../components/DateTimeFields";
import QRScanModal from "../components/QRScanModal";
import { api } from "../lib/api";
import { getRegion, regionCenter, setRegion as saveRegion, countryName } from "../lib/region";
import RegionChip from "../components/RegionChip";
import CountryPicker from "../components/CountryPicker";
import { setPageTitle } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { fadeUp, revealOnce, staggerParent } from "../lib/motion";

// The 3D scene is code-split: the homepage's first paint never waits on three.js.
// If the chunk itself fails (classic stale-deploy hash mismatch after a redeploy),
// hard-reload ONCE to pick up the fresh bundle; a genuine second failure surfaces
// the real error and falls through to the static fallback via SceneCrashGuard.
const CommercialVignette = lazy(() =>
  import(/* webpackChunkName: "commercial-vignette" */ "../components/home/CommercialVignette")
);
const ParkingScene = lazy(() =>
  import("../components/home/ParkingScene").catch((err) => {
    console.error("[Home] 3D chunk load failed:", err);
    if (typeof window !== "undefined" && !sessionStorage.getItem("mrp-3d-reload")) {
      sessionStorage.setItem("mrp-3d-reload", "1");
      window.location.reload();
      return new Promise(() => {});
    }
    throw err;
  })
);

// Scene-level crash guard: a 3D failure first degrades ONCE to the low tier
// (drops reflector/shadows — the high-tier-only surfaces) and only if that also
// fails shows the premium static fallback, with the real error logged either way.
class SceneCrashGuard extends Component {
  constructor(props) { super(props); this.state = { failed: false, retried: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) {
    console.error("[Home] 3D scene error:", err);
    if (!this.state.retried && this.props.onDegrade) {
      this.setState({ failed: false, retried: true });
      this.props.onDegrade();
    }
  }
  render() { return this.state.failed ? <SceneFallback /> : this.props.children; }
}

// One authoritative text timeline (V6 Part 4). Copy preserved from the
// existing approved beats; each scene owns a deterministic visibility window
// derived ONLY from master progress — forward, backward, and fast scrolls all
// settle correctly, and unrelated scenes can never collide.
const SCENE_COPY = [
  { id: "find", at: 0.06, eyebrow: "Find", title: "Spaces around you, right now." },
  { id: "choose", at: 0.15, eyebrow: "Choose", title: "Choose your space." },
  { id: "availability", at: 0.25, eyebrow: "Availability", title: "Green is yours to take." },
  { id: "reserve", at: 0.36, eyebrow: "Reserve", title: "Lock it for your time." },
  { id: "payment", at: 0.48, eyebrow: "Payment", title: "Payment holds, not surprises.", sub: "Your card is authorized at booking — charged only after your arrival is verified with a one-time code." },
  { id: "arrive", at: 0.61, eyebrow: "Arrive", title: "Arrive with confidence." },
  { id: "approach", at: 0.74, eyebrow: "Approach", title: "Your space is ready." },
  { id: "park", at: 0.86, eyebrow: "Park", title: "Your MyRedPark space." },
  { id: "host", at: 0.94, eyebrow: "Host", title: "Turn unused space into opportunity." },
  { id: "final", at: 0.978, eyebrow: "MyRedPark", title: "Your spot. Your way.", hold: true },
];

const CITY_IMG = "https://images.unsplash.com/photo-1514439827219-9137a0b99245?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const STEPS = [
  { n: "01", icon: Search, t: "Search", d: "Find real parking spaces near your destination — listed by the people who own them." },
  { n: "02", icon: MousePointerClick, t: "Select", d: "Choose your spot and time. Compare price, space type, and amenities." },
  { n: "03", icon: CalendarCheck, t: "Reserve", d: "Book securely in seconds. Your card is authorized — never charged early." },
  { n: "04", icon: Car, t: "Park", d: "Arrive, show your one-time code, and enjoy stress-free parking." },
];

const TRUST = [
  { icon: MapPin, t: "REAL SPACES", s: "Live availability" },
  { icon: ShieldCheck, t: "SECURE PAYMENTS", s: "Protected by Stripe" },
  { icon: KeyRound, t: "VERIFIED ARRIVAL", s: "One-time code check-in" },
  { icon: CalendarDays, t: "FLEXIBLE BOOKINGS", s: "Hourly, daily, monthly" },
];

const CHIPS = [
  { label: "Near me", icon: LocateFixed, action: "nearme" },
  { label: "City centre", icon: MapPin, q: "Downtown Toronto, ON" },
  { label: "Underground", icon: Car, type: "Underground" },
  { label: "Covered", icon: ShieldCheck, type: "Covered" },
];

// Scroll-beat copy overlay — presentation only, pointer-events disabled so the
// scene never blocks interaction.
// Per-scene copy item: enter = fade + rise 20px, exit = fade + lift -12px.
// The exit window ends BEFORE the next scene's window begins (crossfade gap),
// so two unrelated scenes can never both be visible.
function SceneCopyItem({ progress, item, nextAt }) {
  const inStart = item.at - 0.035;
  const outStart = item.hold ? 1.0 : nextAt - 0.05;
  const outEnd = item.hold ? 1.05 : nextAt - 0.015;
  const opacity = useTransform(progress, [inStart, item.at, outStart, outEnd], [0, 1, 1, item.hold ? 1 : 0]);
  const y = useTransform(progress, [inStart, item.at, outStart, outEnd], [20, 0, 0, item.hold ? 0 : -12]);
  return (
    <motion.div style={{ opacity, y }}
                className="absolute left-[6%] sm:left-12 bottom-[max(5.5rem,env(safe-area-inset-bottom))] sm:bottom-28 max-w-[88vw] sm:max-w-md pointer-events-none"
                data-testid={`scene-beat-${item.id}`} aria-hidden="true">
      <p className="section-label">{item.eyebrow}</p>
      <p className="font-display font-bold tracking-tight text-white mt-2 text-[clamp(1.35rem,1.1rem+1.6vw,2.25rem)]"
         style={{ textShadow: "0 2px 24px rgba(0,0,0,0.85)" }}>{item.title}</p>
      {item.sub && (
        <p className="mt-2.5 text-sm text-zinc-300 leading-relaxed max-w-[84vw] sm:max-w-sm"
           style={{ textShadow: "0 1px 14px rgba(0,0,0,0.85)" }}>{item.sub}</p>
      )}
    </motion.div>
  );
}

// Rendered INSIDE the sticky viewport so copy is viewport-fixed for the whole
// track (V6 root cause: beats were anchored to the bottom of the 500vh track).
function SceneCopyLayer({ progress }) {
  return SCENE_COPY.map((item, i) => (
    <SceneCopyItem key={item.id} progress={progress} item={item} nextAt={SCENE_COPY[i + 1]?.at ?? 1.05} />
  ));
}

// Premium static fallback: no WebGL or prefers-reduced-motion (Part 9).
function SceneFallback() {
  return (
    <div className="absolute inset-0" data-testid="scene-static-fallback">
      <img src="/brand/hero-home.jpg" alt="" aria-hidden="true" loading="lazy" className="w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,7,10,0.6) 0%, rgba(5,7,10,0.4) 45%, rgba(5,7,10,0.94) 100%)" }} />
      <div className="absolute left-5 sm:left-12 bottom-24 sm:bottom-28">
        <p className="section-label">Park</p>
        <p className="font-display font-bold text-2xl sm:text-4xl tracking-tight text-white mt-2"
           style={{ textShadow: "0 2px 24px rgba(0,0,0,0.85)" }}>Arrive with confidence.</p>
      </div>
    </div>
  );
}

// Premium branded loading state (Part 26) — shown while the 3D chunk loads.
function SceneLoader() {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#05070A]" data-testid="scene-loader" role="status" aria-label="Loading parking experience">
      <p className="font-display font-extrabold tracking-tight text-2xl">MY<span className="text-[#E50914]">RED</span>PARK</p>
      <p className="text-xs text-zinc-500 mt-2">Preparing your parking experience…</p>
      <div className="mt-6 w-44 h-0.5 bg-[#1F2328] rounded-full overflow-hidden" aria-hidden="true">
        <div className="h-full w-1/3 bg-[#E50914] rounded-full animate-[loader-slide_1.1s_ease-in-out_infinite]" />
      </div>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [spots, setSpots] = useState(null);
  const [query, setQuery] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [place, setPlace] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [searchError, setSearchError] = useState("");
  const [propCode, setPropCode] = useState("");
  const [sugg, setSugg] = useState([]);
  const [suggOpen, setSuggOpen] = useState(false);
  const [suggLoading, setSuggLoading] = useState(false);
  const [suggActive, setSuggActive] = useState(-1);
  const suggTimer = useRef(null);
  const reduced = useReducedMotion();
  const sceneTrackRef = useRef(null);
  // Scene progress drives car + cameras; the sticky viewport holds the canvas
  // while the 500vh track supplies scroll distance. Scrolling up reverses naturally.
  const { scrollYProgress: sceneProgress } = useScroll({ target: sceneTrackRef, offset: ["start start", "end end"] });
  // WebGL capability probe — no-WebGL devices get the premium static fallback.
  const [webglOk] = useState(() => {
    try {
      const c = document.createElement("canvas");
      return !!(c.getContext("webgl2") || c.getContext("webgl"));
    } catch { return false; }
  });
  const sceneQuality = typeof window !== "undefined" && window.innerWidth < 1024 ? "low" : "high";
  const [qualityOverride, setQualityOverride] = useState(null); // crash-degrade retry (high → low)
  // ?force3d=1 — diagnostic override: forces FULL motion even when the OS
  // requests reduced motion (never bypasses a genuine WebGL failure).
  const force3d = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("force3d");
  // Motion preference is a MOTION-INTENSITY choice — never a 3D on/off switch.
  // WebGL available → the live 3D scene ALWAYS renders; "reduced" only strips
  // nonessential animation (idle float/drift/inertia). Only genuine no-WebGL
  // or an unrecoverable renderer failure reaches the static image fallback.
  const motionMode = reduced && !force3d ? "reduced" : "full";
  const live3d = webglOk;
  // Gated diagnostics (?debug3d=1) — inert in normal production use (request 23).
  useEffect(() => {
    if (typeof window === "undefined" || !new URLSearchParams(window.location.search).has("debug3d")) return undefined;
    const t = setTimeout(() => {
      const canvas = document.querySelector('[data-testid="parking-scene-canvas"] canvas');
      let renderer = "unknown";
      try {
        const gl = document.createElement("canvas").getContext("webgl");
        const ext = gl && gl.getExtension("WEBGL_debug_renderer_info");
        if (ext) renderer = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL);
      } catch { /* keep "unknown" */ }
      const mrp = window.__mrp3d?.info?.() || {};
      const p = sceneProgress.get();
      console.info("[MRP-3D-DIAG]", {
        buildId: document.querySelector('script[src*="main."]')?.src.split("/").pop() || "dev",
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        userAgent: navigator.userAgent,
        webglSupported: webglOk,
        renderer,
        qualityTier: qualityOverride || sceneQuality,
        motionPreference: reduced ? "reduce" : "no-preference",
        motionMode,
        force3d,
        fallbackActive: !live3d,
        fallbackReason: !webglOk ? "no-webgl" : "none",
        canvasMounted: !!canvas,
        canvasWidth: canvas?.width || 0,
        canvasHeight: canvas?.height || 0,
        renderCalls: mrp.calls ?? 0,
        triangles: mrp.triangles ?? 0,
        sceneReady: !!canvas && (mrp.calls ?? 0) > 0,
        scrollProgress: +p.toFixed(3),
      });
    }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Hero copy/search overlay: fully usable at rest, hands the stage to the car
  // as the story begins, and returns when scrolling back up.
  const overlayFade = useTransform(sceneProgress, [0, 0.085], [1, 0]);
  const overlayY = useTransform(sceneProgress, [0, 0.085], [0, -60]);
  const [overlayGone, setOverlayGone] = useState(false);
  useMotionValueEvent(sceneProgress, "change", (v) => setOverlayGone(v > 0.09));

  useEffect(() => setPageTitle(""), []);
  // Region-first discovery: resolve the visitor's country (server-side IP
  // detection or their deliberate choice), then load that country's listings
  // around the country's default center. The server enforces the filter.
  const [region, setRegion] = useState(undefined); // undefined = detecting
  useEffect(() => { getRegion().then(setRegion); }, []);
  useEffect(() => {
    if (region === undefined) return;
    const c = regionCenter(region.country);
    api.nearby(c.lat, c.lng, 25).then(setSpots).catch(() => setSpots([]));
  }, [region]);

  const onQuery = (v) => {
    setQuery(v);
    setPlace(null);
    setSuggActive(-1);
    clearTimeout(suggTimer.current);
    if (v.trim().length < 3) { setSugg([]); setSuggOpen(false); setSuggLoading(false); return; }
    setSuggLoading(true);
    suggTimer.current = setTimeout(async () => {
      try {
        const r = await api.geoAutocomplete(v);
        setSugg(r.suggestions || []);
        setSuggOpen(true);
      } catch { setSugg([]); }
      finally { setSuggLoading(false); }
    }, 280);
  };

  const pickPlace = (s) => { setPlace(s); setQuery(s.main_text || s.description); setSuggOpen(false); setSuggActive(-1); };

  const onLocKeyDown = (e) => {
    if (!suggOpen || !sugg.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setSuggActive((i) => (i + 1) % sugg.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSuggActive((i) => (i - 1 + sugg.length) % sugg.length); }
    else if (e.key === "Enter" && suggActive >= 0) { e.preventDefault(); pickPlace(sugg[suggActive]); }
    else if (e.key === "Escape") { setSuggOpen(false); setSuggActive(-1); }
  };

  // Local (browser) "today" — date inputs operate in the user's timezone.
  const goSearch = (e) => {
    e?.preventDefault();
    setSearchError("");
    const filled = [startDate, startTime, endDate, endTime].filter(Boolean).length;
    if (filled > 0 && filled < 4) { setSearchError("Please select a start date & time and an end date & time."); return; }
    if (filled === 4) {
      const s = new Date(`${startDate}T${startTime}:00`), en = new Date(`${endDate}T${endTime}:00`);
      if (s < new Date()) { setSearchError("Start can't be in the past — pick a time from now onwards."); return; }
      if (en <= s) { setSearchError("End must be later than start — even by 15 minutes."); return; }
    }
    const p = new URLSearchParams();
    if (place?.description || query.trim()) p.set("q", place?.description || query.trim());
    if (place?.lat != null && place?.lng != null) { p.set("lat", place.lat); p.set("lng", place.lng); }
    if (filled === 4) { p.set("start_date", startDate); p.set("start_time", startTime); p.set("end_date", endDate); p.set("end_time", endTime); }
    navigate(`/find${p.toString() ? `?${p}` : ""}`);
  };

  // Property code entry (V2): code only navigates to the canonical public
  // route — resolution, availability and privacy are enforced server-side.
  const goPropertyCode = () => {
    const c = propCode.trim();
    if (!/^\d{4}$/.test(c)) { setSearchError("Property codes are 4 digits — check the parking sign or your invite."); return; }
    setSearchError("");
    navigate(`/p/${c}`);
  };

  const chip = (c) => {
    if (c.action === "nearme") {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => navigate(`/find?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}&q=${encodeURIComponent("Near me")}`),
        () => navigate("/find"),
        { timeout: 6000 }
      );
      return;
    }
    if (c.q) { navigate(`/find?q=${encodeURIComponent(c.q)}`); return; }
    if (c.type) { navigate(`/find?type=${encodeURIComponent(c.type)}`); }
  };

  return (
    <div className="min-h-screen flex flex-col grain" data-testid="home-page">
      <Navbar />
      <main className="flex-1">
        {/* ============ HERO — real 3D cinematic scene, scroll-driven ============ */}
        <section ref={sceneTrackRef}
                 className={live3d ? "relative h-[500vh] bg-[#05070A]" : "relative min-h-[100svh] flex flex-col overflow-hidden bg-[#05070A]"}
                 data-testid="hero-3d-canvas-container" aria-label="MyRedPark — cinematic parking experience">
          {/* scene layer */}
          <div className={live3d ? "sticky top-0 h-screen overflow-hidden" : "absolute inset-0"}>
            {live3d ? (
              <SceneCrashGuard onDegrade={() => setQualityOverride("low")}>
                <Suspense fallback={<SceneLoader />}>
                  <ParkingScene
                    progress={sceneProgress}
                    quality={qualityOverride || sceneQuality}
                    motionMode={motionMode}
                  />
                </Suspense>
              </SceneCrashGuard>
            ) : (
              <SceneFallback />
            )}
      {/* readability scrims — tuned for the daylight scene; never block interaction */}
            <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
                 style={{ background: "linear-gradient(180deg, rgba(10,16,24,0.5) 0%, rgba(10,16,24,0.12) 30%, rgba(10,16,24,0.08) 55%, rgba(10,16,24,0.45) 100%)" }} />
            <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
                 style={{ background: "radial-gradient(ellipse 64% 56% at 32% 60%, rgba(8,12,18,0.42) 0%, transparent 72%)" }} />
            {/* scene-synchronized copy layer — viewport-fixed inside the sticky container */}
            {live3d && <SceneCopyLayer progress={sceneProgress} />}
          </div>

          {/* hero copy + search overlay — HTML in the DOM for SEO/accessibility */}
          <motion.div style={live3d ? { opacity: overlayFade, y: overlayY, pointerEvents: overlayGone ? "none" : "auto" } : {}}
                      className={`${live3d ? "sticky top-0 h-screen -mt-[100vh]" : "relative flex-1"} z-10 flex flex-col justify-center max-sm:justify-start max-sm:pt-28 max-w-7xl w-full mx-auto px-5 sm:px-8 pt-32 pb-10`}
                      data-testid="hero-overlay">
            <motion.h1 variants={fadeUp} initial="hidden" animate="show" className="type-hero max-w-4xl" data-testid="hero-headline">
              Parking<br /><span className="text-[#FF3B45]">made simple.</span>
            </motion.h1>
            <motion.p variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.1 }}
                      className="mt-6 text-base sm:text-lg text-zinc-300 max-w-xl leading-relaxed" data-testid="hero-subcopy">
              Find and book real parking spaces from real people.<br className="hidden sm:block" />{" "}
              Drive with confidence. Park on your terms.
            </motion.p>

            {/* Unified glass search card — Where / When / Find Parking */}
            <motion.form variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.2 }}
                         onSubmit={goSearch} data-testid="hero-search"
                         className="mt-10 max-w-5xl rounded-3xl border border-white/[0.14] bg-[#0D1117]/80 backdrop-blur-2xl shadow-[0_24px_60px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.06)] p-3 sm:p-3.5 transition-shadow duration-300 hover:shadow-[0_28px_70px_rgba(0,0,0,0.6),0_0_40px_rgba(229,9,20,0.08)]">
              <div className="grid gap-2.5 md:grid-cols-[1.5fr_1fr_1fr_1fr_1fr_auto] md:items-stretch">
                {/* WHERE */}
                <div className="relative rounded-xl border border-white/10 bg-[#161B22]/80 px-3.5 py-2 transition-colors focus-within:border-[#E50914]/70 focus-within:ring-2 focus-within:ring-[#E50914]/25">
                  <label htmlFor="hero-loc" className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.14em] text-zinc-500">
                    <MapPin className="w-3.5 h-3.5 text-[#FF3B45]" /> Where
                  </label>
                  <div className="mt-0.5 flex items-center gap-2">
                    <input id="hero-loc" data-testid="hero-search-input" value={query} onChange={(e) => onQuery(e.target.value)}
                           onFocus={() => sugg.length && setSuggOpen(true)} onBlur={() => setTimeout(() => setSuggOpen(false), 150)}
                           onKeyDown={onLocKeyDown}
                           placeholder="City, address, or landmark" autoComplete="off" aria-label="Location"
                           aria-expanded={suggOpen} aria-controls="hero-suggestions" aria-autocomplete="list"
                           aria-activedescendant={suggActive >= 0 ? `hero-sugg-${suggActive}` : undefined}
                           role="combobox"
                           className="w-full bg-transparent text-sm font-semibold text-white placeholder:text-zinc-600 placeholder:font-normal focus:outline-none" />
                    {suggLoading && <Loader2 className="w-3.5 h-3.5 text-zinc-500 animate-spin shrink-0" aria-hidden="true" />}
                    {query && !suggLoading && (
                      <button type="button" onClick={() => { setQuery(""); setPlace(null); setSugg([]); setSuggOpen(false); }}
                              data-testid="hero-search-clear" aria-label="Clear location"
                              className="text-zinc-500 hover:text-white transition-colors shrink-0">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {suggOpen && sugg.length > 0 && (
                    <div id="hero-suggestions" className="addr-suggestions card-dark p-1.5 shadow-2xl z-[60] max-h-60 overflow-auto" data-testid="hero-search-suggestions" role="listbox">
                      {sugg.map((s, i) => (
                        <button key={s.place_id} type="button" role="option" id={`hero-sugg-${i}`} aria-selected={i === suggActive}
                                data-testid="hero-search-option"
                                className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors ${i === suggActive ? "bg-[#E50914]/15 ring-1 ring-[#E50914]/40" : "hover:bg-white/5"}`}
                                onMouseDown={() => pickPlace(s)}>
                          <span className="block text-sm font-medium text-white line-clamp-1">{s.main_text}</span>
                          {s.secondary_text && <span className="block text-xs text-zinc-500 line-clamp-1">{s.secondary_text}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* WHEN — same premium popover fields as checkout (byte-identical values) */}
                <DateField label="Start date" value={startDate} onChange={(v) => { setStartDate(v); setSearchError(""); }} minDate={new Date()} testId="hero-search-start-date" />
                <TimeField label="Start time" value={startTime} onChange={(v) => { setStartTime(v); setSearchError(""); }} date={startDate} testId="hero-search-start-time" />
                <DateField label="End date" value={endDate} onChange={(v) => { setEndDate(v); setSearchError(""); }} minDate={startDate ? new Date(`${startDate}T00:00:00`) : new Date()} rangeStart={startDate} rangeEnd={endDate} testId="hero-search-end-date" />
                <TimeField label="End time" value={endTime} onChange={(v) => { setEndTime(v); setSearchError(""); }} date={endDate} testId="hero-search-end-time" />
                {/* GO */}
                <button type="submit" data-testid="hero-find-parking-cta" aria-label="Find parking"
                        className="h-full min-h-14 md:min-h-0 md:w-auto px-6 rounded-xl bg-gradient-to-r from-[#E50914] to-[#FF3B45] text-white font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-150 hover:shadow-[0_0_28px_rgba(229,9,20,0.45)] active:scale-[0.97]">
                  <Search className="w-4 h-4" /> Find Parking
                </button>
              </div>
            </motion.form>
            {searchError && (
              <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} role="alert" data-testid="hero-search-error"
                        className="mt-3 text-sm font-medium text-[#FF6B6B]">{searchError}</motion.p>
            )}

            {/* Quick filters — real behavior only */}
            <motion.div variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.3 }} className="mt-5 flex flex-wrap gap-2.5">
              {CHIPS.map((c) => (
                <button key={c.label} onClick={() => chip(c)} data-testid={`hero-chip-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/[0.04] backdrop-blur-md text-xs font-medium text-zinc-300 hover:border-[#E50914]/50 hover:text-white transition-all duration-200">
                  <c.icon className="w-3.5 h-3.5 text-[#FF3B45]" />{c.label}
                </button>
              ))}
              <Link to={user ? "/host/listings/new" : "/register"} data-testid="hero-list-space-cta"
                    className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium text-[#FF3B45] hover:text-white transition-colors whitespace-nowrap shrink-0">
                Have a space? Become a host <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </motion.div>

            {/* Property code entry — commercial properties (V2). Code only ever
                navigates to the canonical public route; resolution is server-side. */}
            <motion.div variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.38 }}
                        className="mt-4 flex flex-wrap items-center gap-3" data-testid="hero-property-code">
              <span className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">Have a property code?</span>
              <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#0D1117]/80 backdrop-blur-xl px-3 py-1.5 transition-colors focus-within:border-[#E50914]/70">
                <KeyRound className="w-3.5 h-3.5 text-[#FF3B45]" aria-hidden="true" />
                <input value={propCode} onChange={(e) => setPropCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
                       onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); goPropertyCode(); } }}
                       placeholder="4827" inputMode="numeric" maxLength={4} aria-label="4-digit property code"
                       data-testid="hero-property-code-input"
                       className="w-14 bg-transparent text-sm font-bold tracking-[0.2em] text-white placeholder:text-zinc-700 focus:outline-none" />
                <button type="button" onClick={goPropertyCode} data-testid="hero-property-code-go"
                        className="text-xs font-semibold text-[#FF3B45] hover:text-white transition-colors">
                  Open property
                </button>
              </div>
              <button type="button" onClick={() => setScanOpen(true)} data-testid="hero-scan-qr"
                      className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#0D1117]/80 backdrop-blur-xl px-3 py-2 text-xs font-semibold text-zinc-300 hover:text-white hover:border-[#E50914]/70 transition-colors">
                <QrCode className="w-3.5 h-3.5 text-[#FF3B45]" aria-hidden="true" /> Scan QR Code
              </button>
            </motion.div>
          </motion.div>

          {/* scroll-beat copy now lives inside the sticky scene layer (V6) */}
        </section>

        {/* Static story strip — reduced-motion / no-WebGL mode keeps the full
            product story readable (V6 Part 20/26) */}
        {!live3d && (
          <section className="relative border-t border-white/[0.07] bg-[#0A0D12]" aria-label="How MyRedPark works">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {SCENE_COPY.filter((s) => s.id !== "final").map((s) => (
                <div key={s.id} className="card-dark p-5" data-testid={`static-beat-${s.id}`}>
                  <p className="section-label">{s.eyebrow}</p>
                  <p className="mt-2 text-sm text-zinc-300 font-medium">{s.title}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Trust strip — hairline columns */}
        <section className="relative border-t border-white/[0.07] bg-black/30 backdrop-blur-md" data-testid="trust-strip">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4">
              {TRUST.map((t, i) => (
                <div key={t.t} className={`flex items-center gap-3.5 py-5 ${i > 0 ? "lg:pl-8 lg:border-l border-white/[0.07]" : ""} ${i >= 2 ? "max-lg:border-t max-lg:border-white/[0.07]" : ""} ${i % 2 === 1 ? "max-lg:pl-6 max-lg:border-l max-lg:border-white/[0.07]" : ""}`}>
                  <t.icon className="w-5 h-5 text-zinc-400 shrink-0" aria-hidden="true" />
                  <div>
                    <p className="text-[0.68rem] font-bold tracking-[0.16em] text-white">{t.t}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{t.s}</p>
                  </div>
                </div>
              ))}
            </div>
        </section>

        {/* ============ HOW IT WORKS — light editorial ============ */}
        <section id="how-it-works" className="light-section py-24 sm:py-32" data-testid="section-how-it-works">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <motion.p variants={fadeUp} {...revealOnce} className="section-label">How it works</motion.p>
            <div className="mt-6 grid lg:grid-cols-2 gap-10 items-end">
              <motion.h2 variants={fadeUp} {...revealOnce} className="light-h2">
                From search to your spot.<br />In <span className="text-[#E50914]">four simple steps.</span>
              </motion.h2>
              <motion.div variants={fadeUp} {...revealOnce}>
                <p className="text-[#52555A] leading-relaxed max-w-md">
                  A seamless experience for drivers. A smarter way to earn for hosts. Parking should just work — and now it does.
                </p>
                <Link to={user ? "/find" : "/register"} className="btn-ink mt-7" data-testid="section-find-cta">
                  Get Started <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </div>
            <motion.div variants={staggerParent(0.1)} {...revealOnce} className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
              {STEPS.map((s, i) => (
                <motion.div key={s.n} variants={fadeUp} className="relative" data-testid={`step-${s.t.toLowerCase()}`}>
                  {i < STEPS.length - 1 && <div className="step-connector hidden lg:block absolute top-[2.1rem] left-[4.75rem] right-[-1.5rem]" aria-hidden="true" />}
                  <div className="step-icon"><s.icon className="w-6 h-6" aria-hidden="true" /></div>
                  <p className="font-mono text-[0.68rem] tracking-[0.2em] text-[#9A9EA3] mt-6">{s.n}</p>
                  <h3 className="font-display font-bold text-xl text-[#0B0D0F] tracking-tight mt-1.5">{s.t}</h3>
                  <p className="text-sm text-[#5C6066] leading-relaxed mt-2 max-w-[16rem]">{s.d}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ============ LIVE MARKETPLACE — real data ============ */}
        <section className="py-20 sm:py-28" data-testid="section-live-marketplace">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <motion.div variants={staggerParent()} {...revealOnce}>
              <motion.p variants={fadeUp} className="section-label">Live marketplace</motion.p>
              <div className="mt-5 flex flex-wrap items-end justify-between gap-6">
                <motion.h2 variants={fadeUp} className="type-display max-w-lg">Real spaces, listed by real hosts.</motion.h2>
                <motion.div variants={fadeUp} className="flex items-center gap-3">
                  <RegionChip region={region} onChange={setRegion} testId="home-region-chip" />
                  <Link to="/find" className="btn-ghost-red" data-testid="browse-all-spots">Browse every space <ArrowRight className="w-4 h-4" /></Link>
                </motion.div>
              </div>
            </motion.div>
            <div className="mt-12">
              {spots === null || region === undefined ? <CardSkeleton count={4} /> : spots.length === 0 ? (
                <div className="border border-dashed border-[#27272A] rounded-2xl py-20 text-center px-6" data-testid="no-nearby-spots">
                  {region && !region.country ? (
                    <>
                      <p className="type-h3">Choose your country to see parking near you.</p>
                      <p className="mt-2 text-sm text-zinc-500 max-w-md mx-auto">We couldn't determine your region automatically. Pick your country — no precise location needed.</p>
                      <div className="max-w-xs mx-auto mt-6 text-left">
                        <CountryPicker value="" onChange={async (cc) => setRegion(await saveRegion(cc))} testId="region-fallback-picker" />
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="type-h3">No parking spaces are currently available in {countryName(region?.country)}.</p>
                      <p className="mt-2 text-sm text-zinc-500">Hosts are joining. {user ? "List yours and be first." : "Create an account and list yours."}</p>
                    </>
                  )}
                </div>
              ) : (
                <motion.div variants={staggerParent(0.07)} {...revealOnce}
                            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="nearby-spots">
                  {spots.slice(0, 4).map((s) => (
                    <motion.div key={s.spot_id} variants={fadeUp}><SpotCard spot={s} /></motion.div>
                  ))}
                </motion.div>
              )}
            </div>
          </div>
        </section>

        {/* ============ ROLE CHOICE — one decision at a time ============ */}
        <section className="relative border-t border-[#141619] bg-black/40" data-testid="section-role-choice" aria-label="Choose your mode">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-16">
            <motion.div variants={staggerParent(0.08)} {...revealOnce} className="grid sm:grid-cols-3 gap-4">
              {[
                { label: "Driver", desc: "Scan a QR, search an address, or enter a 4-digit code — park in seconds.", to: user ? "/find" : "/register", tid: "role-driver", Icon: Car },
                { label: "Host", desc: "Your empty space is inventory. List it once — we handle booking, verification and payment.", to: user ? "/host" : "/register", tid: "role-host", Icon: KeyRound },
                { label: "Commercial", desc: "Properties, categories, capacity, pricing and payouts — one console.", to: user ? "/commercial" : "/register", tid: "role-commercial", Icon: Building2 },
              ].map(({ label, desc, to, tid, Icon }) => (
                <motion.div key={label} variants={fadeUp}>
                  <Link to={to} data-testid={tid}
                        className="card-dark card-dark-hover p-6 flex flex-col gap-3 group h-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#E50914]">
                    <div className="w-10 h-10 rounded-xl border border-[#27272A] bg-[#0D1114] flex items-center justify-center">
                      <Icon className="w-4.5 h-4.5 text-[#FF3B45]" />
                    </div>
                    <h3 className="font-display font-bold text-lg tracking-tight group-hover:text-[#FF3B45] transition-colors">{label}</h3>
                    <p className="text-sm text-zinc-500 leading-relaxed">{desc}</p>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ============ COMMERCIAL — 3D vignette ============ */}
        <CommercialVignette />

        {/* ============ MOBILE APPS — cinematic 3D teaser ============ */}
        <MobileAppsComingSoon />

        {/* ============ FINAL CTA ============ */}
        <section className="relative py-32 sm:py-44 overflow-hidden border-t border-[#141619]">
          <img src={CITY_IMG} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(8,11,13,0.92) 0%, rgba(8,11,13,0.78) 50%, #080B0D 100%)" }} />
          <div className="relative max-w-7xl mx-auto px-5 sm:px-8 text-center">
            <motion.p variants={fadeUp} {...revealOnce} className="section-label justify-center" data-testid="mobile-app-coming-soon">
              One account — website &amp; mobile app (coming soon)
            </motion.p>
            <motion.h2 variants={fadeUp} {...revealOnce} className="type-hero mt-7">Your spot.<br />Your way.</motion.h2>
            <motion.div variants={fadeUp} {...revealOnce} className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/find" className="btn-primary !px-10 !py-4">Find Parking</Link>
              <Link to={user ? "/host/listings/new" : "/register"} className="btn-secondary !px-10 !py-4 !bg-black/40 backdrop-blur-md">List Your Space</Link>
            </motion.div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
          <p className="text-xs text-zinc-600 leading-relaxed max-w-3xl" data-testid="tax-disclaimer">
            Tax note: depending on your jurisdiction and activity, drivers and hosts may have tax obligations. Hosts may have
            GST/HST/VAT or sales-tax obligations under applicable law, and MyRedPark may have its own statutory tax and
            reporting obligations where applicable. Tax treatment differs by jurisdiction — please seek professional tax
            advice where appropriate.
          </p>
        </section>
      </main>
      <QRScanModal open={scanOpen} onClose={() => setScanOpen(false)} />
      <Footer />
    </div>
  );
}
